import {
  addQueryStringParameterToUrl,
  ApiError,
  Configuration,
  fetchOptions,
  OptionItem,
  postOption,
  sortOptions,
} from "@interface48/api";
import { createStyles, Theme, WithStyles, withStyles, WithTheme } from "@material-ui/core/styles";
import classNames from "classnames";
import React from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import { ActionMeta, ValueType } from "react-select/src/types";
import {
  SelectAsyncRemoteOptions,
  SelectCreateOptions,
  SelectMultipleOptions,
  SelectRemoteOptions,
  SelectValue,
} from "./models";
import { getMuiStyles, MuiComponents } from "./react-select";
import { getSelectedOptionItem } from "./utils";

const SELECT_ALL_OPTION_VALUE = "*";
const SELECT_ALL_OPTION_LABEL = "(Select All)";
const VALUE_QUERY_STRING_PARAM_KEY = "value";
const FILTER_TERM_QUERY_STRING_PARAM_KEY = "filterTerm";
const MAXIMUM_RESULTS_COUNT_QUERY_STRING_PARAM_KEY = "maximumResultsCount";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      position: "relative",
      marginTop: 14,
    },
  });

interface ReactSelectProps<T> extends WithTheme, WithStyles<typeof styles> {
  id: string;
  label?: string;
  placeholder?: string;
  autofocus?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  value?: SelectValue<T>;
  options?: Array<OptionItem<T>>;
  optionGroups?: Array<{ name: string; values: T[] }>;
  enumDisabled?: Array<T>;
  multipleOptions?: SelectMultipleOptions;
  remoteOptions?: SelectRemoteOptions | SelectAsyncRemoteOptions;
  createOptions?: SelectCreateOptions;
  // sharedValidation may be used by consuming libraries, i.e. rjsf-forms
  sharedValidation?: { addError: (propertyPath: string, error?: string) => void };
  className?: string;
  getApiConfiguration?: () => Promise<Configuration>;
  processValue?: (value: T) => T | null | undefined;
  onChange: (value: SelectValue<T>, id?: string, otherData?: any) => void;
  onBlur?: (value: SelectValue<T>, otherData?: any) => void;
  onFocus?: (value: SelectValue<T>, otherData?: any) => void;
}

interface ReactSelectState<T> {
  value?: SelectValue<T>;
  optionItems: Array<OptionItem<T>>;
  selectedOptionItem?: OptionItem<T> | Array<OptionItem<T>>;
  isLoading?: boolean;
}

export const ReactSelectComponent = class<T extends string | number> extends React.Component<
  ReactSelectProps<T>,
  ReactSelectState<T>
> {
  constructor(props: ReactSelectProps<T>) {
    super(props);

    const { multiple, value, options, remoteOptions } = this.props;

    // If a Remote URL has been specified, then options will be fetched when mounted, and use a copy of the options
    // provided when applicable as we may end up mutating the array
    const optionItems = this.processOptionItems(remoteOptions ? [] : options ? [...options] : [], multiple);

    this.state = { value, optionItems };
  }

  public async componentDidMount() {
    const { multiple, remoteOptions, getApiConfiguration } = this.props;
    const { value } = this.state;

    // If the Select has Remote Options...
    if (remoteOptions) {
      await this.loadRemoteOptions(value, remoteOptions, getApiConfiguration);
    }

    // Try setting the currently Selected Option Item if a value is currently set.
    // WORKAROUND: We are wrapping in a setTimeout here, since otherwise MUI is not showing the label as shrunk
    // if there is a Selected Option Item to display within react-select, resulting in the label being overlaid on top.
    setTimeout(() => {
      const selectedOptionItem = getSelectedOptionItem(this.state.optionItems, this.state.value, multiple);

      if (selectedOptionItem) {
        this.setState({ ...this.state, selectedOptionItem });
      }
    }, 100);
  }

  public render() {
    const {
      id,
      autofocus,
      disabled,
      placeholder,
      optionGroups,
      multiple,
      remoteOptions,
      createOptions,
      classes,
      theme,
    } = this.props;
    const { optionItems, selectedOptionItem, isLoading } = this.state;

    const className = classNames(classes.root, this.props.className);

    const isAsyncSelect = (remoteOptions && (remoteOptions as any).limit != null) || false;

    let renderOptionItems: Array<OptionItem<T>> | Array<{ label: string; options: Array<OptionItem<T>> }>;

    if (optionGroups) {
      const groupedOptionItems: Array<{ label: string; options: Array<OptionItem<T>> }> = [];

      optionGroups.forEach(og => {
        const groupOptionItems = optionItems.filter(oi => og.values.indexOf(oi.value) !== -1);

        if (groupOptionItems.length) {
          groupedOptionItems.push({ label: og.name, options: groupOptionItems });
        }
      });

      const allGroupValues = optionGroups.flatMap(og => og.values);
      const otherOptionItems = optionItems.filter(oi => allGroupValues.indexOf(oi.value) === -1);

      if (otherOptionItems.length) {
        groupedOptionItems.push({ label: "Other", options: otherOptionItems });
      }

      renderOptionItems = groupedOptionItems;
    } else {
      renderOptionItems = optionItems;
    }

    const value = selectedOptionItem != null ? selectedOptionItem : null;

    return createOptions ? (
      <div className={className}>
        <CreatableSelect
          inputId={id}
          styles={getMuiStyles(theme)}
          components={MuiComponents as any}
          menuPortalTarget={document.body}
          isClearable={true}
          placeholder={placeholder}
          autoFocus={autofocus}
          isDisabled={disabled || isLoading}
          isMulti={multiple}
          options={renderOptionItems}
          value={value}
          onCreateOption={this.handleCreateOption}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
        />
      </div>
    ) : isAsyncSelect ? (
      <div className={className}>
        <AsyncSelect
          inputId={id}
          styles={getMuiStyles(theme)}
          components={MuiComponents as any}
          menuPortalTarget={document.body}
          autoFocus={autofocus}
          placeholder={placeholder}
          isClearable={true}
          isDisabled={disabled || isLoading}
          isMulti={multiple}
          defaultOptions={renderOptionItems}
          cacheOptions={(remoteOptions as SelectAsyncRemoteOptions).cache === true}
          loadOptions={this.fetchOptions}
          value={value}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
        />
      </div>
    ) : (
      <div className={className}>
        <Select
          inputId={id}
          styles={getMuiStyles(theme)}
          components={MuiComponents as any}
          menuPortalTarget={document.body}
          isClearable={true}
          placeholder={placeholder}
          autoFocus={autofocus}
          isDisabled={disabled || isLoading}
          isMulti={multiple}
          options={renderOptionItems}
          value={value}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
        />
      </div>
    );
  }

  public async componentDidUpdate(prevProps: ReactSelectProps<T>) {
    const { value: prevValue, options: prevOptions, remoteOptions: prevRemoteOptions } = prevProps;
    const { value, multiple, options, remoteOptions, getApiConfiguration } = this.props;
    const { optionItems } = this.state;

    // If the Options were updated via props...
    if (options && prevOptions !== options) {
      this.loadOptions(value, options);
    }
    // Otherwise, if the Select has Remote Options, check to see if the URL was updated via props...
    else if (prevRemoteOptions && remoteOptions && prevRemoteOptions.url !== remoteOptions.url) {
      this.loadRemoteOptions(value, remoteOptions, getApiConfiguration);
    }
    // Otherwise, if the Value was updated via props...
    else if (prevValue !== value) {
      const isAsyncSelect = (remoteOptions && (remoteOptions as any).limit != null) || false;
      // If this is an Async Select, we do not have access to local Option Items to re-resolve the Selected Option
      // Item, so simply reflect the new value passed in - this is to simply keep the value in state in sync
      // with the value on props, which is what we want when we make a selection from the AsyncSelect, then pass
      // the new value value up to interface48-forms, whic is then passed back down.
      // IMPORTANT: Currently, we do not support externally updating the value for an AsyncSelect, unless that external
      // value is set upon initializing the control (i.e. then we fetch the single corresponding option in
      // componentDidMount)...
      if (isAsyncSelect) {
        this.setState({ ...this.state, value });
      }
      // Otherwise, we have access to local Option Items so we can ensure that the Selected Option Item is in sync with
      // the value passed in via props (i.e. this allows the selection to be changed external to the component)...
      else {
        const nextSelectedOptionItem = getSelectedOptionItem(optionItems, value, multiple);

        this.setState({
          ...this.state,
          value,
          selectedOptionItem: nextSelectedOptionItem,
        });
      }
    }
  }

  public handleChange = (
    selectedOptionItem: ValueType<OptionItem<T | undefined>, boolean>,
    actionMeta: ActionMeta<OptionItem<T | undefined>>,
  ) => {
    const { multiple, multipleOptions, onChange, createOptions } = this.props;
    const { optionItems } = this.state;

    let nextSelectedOptionItem = selectedOptionItem;

    if (multiple && multipleOptions && multipleOptions.selectAll) {
      nextSelectedOptionItem = nextSelectedOptionItem as ReadonlyArray<OptionItem<T | undefined>>;
      if (
        nextSelectedOptionItem == null ||
        !nextSelectedOptionItem[nextSelectedOptionItem.length - 1] ||
        actionMeta.action === "clear"
      ) {
        nextSelectedOptionItem = [];
      } else {
        if (nextSelectedOptionItem[nextSelectedOptionItem.length - 1].value === SELECT_ALL_OPTION_VALUE) {
          nextSelectedOptionItem = optionItems.filter(oi => oi.value !== SELECT_ALL_OPTION_VALUE);
        }
      }
    }

    const nextValue = this.processSelectedOptionItem(nextSelectedOptionItem as OptionItem<T>);

    this.setState(
      {
        ...this.state,
        value: nextValue,
        selectedOptionItem: nextSelectedOptionItem as OptionItem<T> | Array<OptionItem<T>>,
      },
      () => {
        if (createOptions) {
          this.clearCreateOptionValidationError();
        }
        onChange(nextValue);
      },
    );
  };

  public handleBlur = (event: any) => {
    const { onChange, onBlur, createOptions } = this.props;
    const { selectedOptionItem } = this.state;

    const nextValue = this.processSelectedOptionItem(selectedOptionItem);

    if (onBlur) {
      onBlur(nextValue);
    }

    if (createOptions) {
      this.clearCreateOptionValidationError();
    }

    onChange(nextValue);
  };

  public processOptionItems = (optionItems: Array<OptionItem<T>>, multiple: boolean | undefined) => {
    const { multipleOptions } = this.props;

    // If this is a multiple select and there are multipleOptions available, prepend the Select All option...
    if (multiple && multipleOptions && multipleOptions.selectAll) {
      const selectAllOptionItem = { value: SELECT_ALL_OPTION_VALUE, label: SELECT_ALL_OPTION_LABEL } as OptionItem<T>;

      optionItems.unshift(selectAllOptionItem);
    }

    return optionItems;
  };

  public processSelectedOptionItem = (selectedOptionItem?: ValueType<OptionItem<T>, boolean>): SelectValue<T> => {
    const { processValue, multiple } = this.props;

    if (multiple) {
      const multipleSelectedOptionItems = (selectedOptionItem || []) as Array<OptionItem<T>>;

      return multipleSelectedOptionItems.map(selectedOptionValue => {
        const processedValue = processValue
          ? processValue(selectedOptionValue && selectedOptionValue.value)
          : selectedOptionValue && (selectedOptionValue.value as T);

        if (processedValue == null) {
          throw Error("null or undefined key values not supported for multiple Select.");
        }

        return processedValue;
      });
    } else {
      const singleSelectedOptionItem = selectedOptionItem as OptionItem<T>;

      return processValue
        ? processValue(singleSelectedOptionItem && singleSelectedOptionItem.value)
        : singleSelectedOptionItem && (singleSelectedOptionItem.value as T);
    }
  };

  public loadOptions = (value: SelectValue<T> | undefined, options: Array<OptionItem<T>>) => {
    const { multiple } = this.props;

    const nextOptionItems = this.processOptionItems(options, multiple);

    const nextSelectedOptionItem = getSelectedOptionItem(nextOptionItems, value, multiple);

    // WORKAROUND: Corresponding to how we're setting selectedOptionItem in componentDidMount if applicable, we're
    // separately setting the selectedOptionItem after a timeout, because otherwise the MUI label is not displayed
    // shrunk - obviously, would be ideal to understand what's happening in react-select that's causing this
    this.setState({ ...this.state, value, optionItems: nextOptionItems }, () => {
      setTimeout(() => {
        this.setState({ ...this.state, selectedOptionItem: nextSelectedOptionItem });
      }, 100);
    });
  };

  public loadRemoteOptions = async (
    value: SelectValue<T> | undefined,
    remoteOptions: SelectRemoteOptions | SelectAsyncRemoteOptions,
    getApiConfiguration?: () => Promise<Configuration>,
  ) => {
    const { id, label } = this.props;

    const isAsyncSelect = (remoteOptions as any).limit != null;
    // If the Select has a dynamic list of Remote Options (i.e. returned Options filtered by input value)...
    if (isAsyncSelect) {
      const selectAsyncRemoteOptions = remoteOptions as SelectAsyncRemoteOptions;

      try {
        let initialRemoteOptionsUrl = "";
        // If the Select has been initialized with an already selected value, fetch the Single Option corresponding
        // to it, and have that as the default set of Options...
        if (value != null) {
          initialRemoteOptionsUrl = addQueryStringParameterToUrl(selectAsyncRemoteOptions.url, {
            name: VALUE_QUERY_STRING_PARAM_KEY,
            value: `${value}`,
          });
        }
        // Otherwise, if the Select has been initialized with no selection, fetch the first set of Options (up to the
        // specified limit) as the default set of Options...
        else {
          initialRemoteOptionsUrl = addQueryStringParameterToUrl(selectAsyncRemoteOptions.url, {
            name: MAXIMUM_RESULTS_COUNT_QUERY_STRING_PARAM_KEY,
            value: `${selectAsyncRemoteOptions.limit}`,
          });
        }

        const options = await fetchOptions<T>(
          initialRemoteOptionsUrl,
          selectAsyncRemoteOptions.valueType,
          selectAsyncRemoteOptions.defaultLabel,
          selectAsyncRemoteOptions.sort,
          getApiConfiguration,
        );

        this.loadOptions(value, options);
      } catch (error) {
        console.error(error);

        alert(
          `An error occurred upon attempting to initialize the ${label || id} field.\n\n` +
            `Please reload the page and try again. If the error persists, please contact an Administrator for assistance.`,
        );
      }
    }
    // Otherwise, the Select has a fixed list of Remote Options...
    else {
      const selectRemoteOptions = remoteOptions as SelectRemoteOptions;

      try {
        const options = await fetchOptions<T>(
          selectRemoteOptions.url,
          selectRemoteOptions.valueType,
          selectRemoteOptions.defaultLabel,
          selectRemoteOptions.sort,
          getApiConfiguration,
        );

        this.loadOptions(value, options);
      } catch (error) {
        console.error(error);

        alert(
          `An error occurred upon attempting to initialize the ${label || id} field.\n\n` +
            `Please reload the page and try again. If the error persists, please contact an Administrator for assistance.`,
        );
      }
    }
  };

  public fetchOptions = (inputValue: string) => {
    const { remoteOptions, getApiConfiguration } = this.props;

    if (!remoteOptions) {
      throw Error("remoteOptions not specified.");
    }

    const selectAsyncRemoteOptions = remoteOptions as SelectAsyncRemoteOptions;

    let nextRemoteOptionsUrl = selectAsyncRemoteOptions.url;

    if (nextRemoteOptionsUrl) {
      if (inputValue) {
        nextRemoteOptionsUrl = addQueryStringParameterToUrl(nextRemoteOptionsUrl, {
          name: FILTER_TERM_QUERY_STRING_PARAM_KEY,
          value: inputValue,
        });
      }

      if (selectAsyncRemoteOptions.limit) {
        nextRemoteOptionsUrl = addQueryStringParameterToUrl(nextRemoteOptionsUrl, {
          name: MAXIMUM_RESULTS_COUNT_QUERY_STRING_PARAM_KEY,
          value: `${selectAsyncRemoteOptions.limit}`,
        });
      }
    }

    return fetchOptions<T>(
      nextRemoteOptionsUrl,
      selectAsyncRemoteOptions.valueType,
      selectAsyncRemoteOptions.defaultLabel,
      selectAsyncRemoteOptions.sort,
      getApiConfiguration,
    );
  };

  public handleCreateOption = async (value: string) => {
    const { id, createOptions, getApiConfiguration, onChange } = this.props;

    if (!createOptions) {
      throw Error(`handleCreateOption was called for ${id}, but addOption is not specified.`);
    }

    if (this.createOptionPatternMatch(value)) {
      this.setState({ ...this.state, isLoading: true });
      try {
        const addedOption = await postOption<T>(createOptions.url, value, getApiConfiguration);
        this.handleCreatedOption(addedOption);
      } catch (error) {
        this.handleCreateOptionError(error);
      }
    } else {
      this.setState({ ...this.state, value: value as T }, () => {
        onChange(null);
      });
    }
  };

  public handleCreatedOption = (option?: OptionItem<T>) => {
    const { onChange } = this.props;
    let { optionItems } = this.state;

    if (option) {
      let selectedOption: OptionItem<T> | undefined;
      const optionIndex = optionItems.findIndex(o => o.value === option.value);
      // If the option does not yet exist, add the new option and select it...
      if (optionIndex === -1) {
        selectedOption = option;
        optionItems = [...optionItems, selectedOption];
        optionItems = optionItems.sort(sortOptions);
      }
      // Otherwise, if the option has already been added, select it...
      else {
        selectedOption = optionItems[optionIndex];
      }
      this.setState(
        {
          ...this.state,
          value: selectedOption!.value,
          optionItems: optionItems,
          selectedOptionItem: selectedOption,
          isLoading: false,
        },
        () => {
          onChange(option.value);
        },
      );
    }
  };

  public handleCreateOptionError = (error: ApiError) => {
    const { id, label } = this.props;

    alert(`The following error occurred upon attempting to add a new Option for ${label || id}: \n\n${error.message}`);
  };

  public clearCreateOptionValidationError() {
    const { id, sharedValidation } = this.props;

    if (sharedValidation) {
      // Clear any custom validation error added by the handleCreateOption function...
      sharedValidation.addError(id.replace("root_", ""), undefined);
    }
  }

  public createOptionPatternMatch = (value: string) => {
    const { id, createOptions, sharedValidation } = this.props;

    if (createOptions && createOptions.format && createOptions.format.pattern) {
      const match = new RegExp(createOptions.format.pattern).exec("" + value);

      if (sharedValidation) {
        if (match === null) {
          sharedValidation.addError(
            id.replace("root_", ""),
            createOptions.format.errorMessage ||
              `Please input a value that matches the pattern ${createOptions.format.pattern}`,
          );
          return false;
        } else {
          sharedValidation.addError(id.replace("root_", ""), undefined);
          return true;
        }
      }
    }

    return true;
  };
};

export const ReactSelect = withStyles(styles, { withTheme: true })(ReactSelectComponent);
